import { vSelect } from '../../util';

class RecapResponseFormResponseController {
  constructor(
    $scope,
    $timeout,
    $translate,
    showConfirmModal
  ) {
    "ngInject";
    this.$scope = $scope;
    this.$timeout = $timeout;
    this.$translate = $translate;
    this.showConfirmModal = showConfirmModal;
    this._collapsed = false;
    this._vsActivityInited = false;
    this._vsActivityChangeBound = false;
    // Once the user interacts with the product picker (including clearing it),
    // we stop auto-filling it so an explicit clear sticks.
    this._productUserTouched = false;
    // The instance's initial product id (event default or one loaded from an
    // existing response), captured in $onInit so it can be re-seeded if an
    // activity change would otherwise clear it.
    this._initialProductLabelsId = null;
  }

  $onInit() {
    this.$scope.$on('collapseAll', () => this.collapsed = true);
    this.$scope.$on('expandAll', () => this.collapsed = false);
    this.$scope.$on('expandFormsWithErrors', () => {
      if (this.formResponse.hasErrors) {
        this.collapsed = false;
      }
    });
    this.$scope.$on('displayLogicUpdated', () => this._updateVisibleResponses());

    this._updateVisibleResponses();
    this._applyActivityTypeDefault();
    this._applySupplierFromProduct();
    this._applySingleOptionDefaults();

    this._initialProductLabelsId = (this.formResponse && this.formResponse.product_labels_id) || null;

    // Field-level merch-activity visibility (Phase 3): re-filter the rendered
    // fields whenever this instance's selected activity type changes, so fields
    // assigned to specific merchandising activities show/hide live.
    this.$scope.$watch(() => this.formResponse.merchandising_activities_id, () => this._updateVisibleResponses());

    // Re-render the VirtualSelect pickers when either becomes visible and
    // when its underlying option pool shifts. Mirror of the activity-type +
    // product picker handling in RecapResponseFormController._refresh().
    this.$scope.$watch(() => this.showActivityTypePicker, () => {
      this.$timeout(() => this._refreshActivityPicker());
    });
    // Watch the stable underlying source array, not the activityTypeOptions
    // getter: that getter allocates a fresh array of fresh object literals on
    // every call, so $watchCollection (which compares items by reference)
    // would never stabilize and would drive an infinite $digest loop.
    this.$scope.$watchCollection(() => this.form && this.form.merchandising_activities, () => {
      this.$timeout(() => this._refreshActivityPicker());
    });
    // The activity-type options for a merchandising form come from the event's
    // program activities overlaid here; refresh the picker when that pool shifts.
    this.$scope.$watchCollection(() => this.form && this.form._merchandisingEventActivities, () => {
      this.$timeout(() => this._refreshActivityPicker());
    });
    this.$scope.$watch(() => this.showProductPicker, () => {
      this.$timeout(() => this._refreshProductPicker());
    });
    // Same rationale: watch the form's stable products array rather than the
    // productOptions getter, which re-allocates every call. Products arrive
    // asynchronously via the merchandising scope overlay; once present, derive
    // the supplier from a pre-populated product and auto-select a lone product.
    this.$scope.$watchCollection(() => this.form && this.form.products, () => {
      this._applySupplierFromProduct();
      this._applySingleOptionDefaults();
      this.$timeout(() => this._refreshProductPicker());
    });
    // When the selected activity type doesn't use products (e.g. New Account),
    // clear any chosen customer/product so stale values aren't persisted.
    this.$scope.$watch(() => this.selectedActivityAllowsProducts, (allows) => {
      if (!allows) {
        if (this._initialProductLabelsId) {
          const match = (this.form.products || []).find(
            p => Number(p && p.id) === Number(this._initialProductLabelsId)
          );
          this.formResponse.product = match || null;
          this.formResponse._raw_product_labels_id = this._initialProductLabelsId;
          this.formResponse._raw_products_id = null;
          this.formResponse.customers_id = match ? (Number(match.supplier_id) || null) : null;
        } else {
          this.formResponse.customers_id = null;
          this.formResponse._raw_product_labels_id = null;
          this.formResponse._raw_products_id = null;
          this.formResponse.product = null;
        }
      } else {
        // Switched to an activity type that uses products: auto-select a lone
        // product (which derives its supplier) and re-render the picker.
        this._applySingleOptionDefaults();
        this.$timeout(() => this._refreshProductPicker());
      }
    });
  }

  /**
   * Merchandising-type forms get a per-instance activity-type picker. With
   * a single linked activity type the value is auto-set and the picker is
   * hidden; with two or more it's shown so the merchandiser picks per
   * instance.
   *
   * Delegates to FormResponse, the single source of truth shared with save-time
   * validation (FormResponse.validateMerchandisingStaticFields), so the picker
   * shown here is exactly the one validate() enforces.
   */
  get showActivityTypePicker() {
    return this.formResponse.merchShowActivityTypePicker;
  }

  /**
   * Activity types available for this form. Sorted by name for stable UI.
   *
   * @returns {{id:number,name:string}[]}
   */
  get activityTypeOptions() {
    return this.formResponse.merchActivityTypeOptions;
  }

  /**
   * True when the activity-type picker is shown but no activity-type is set.
   * Drives inline error rendering; the same condition (on FormResponse) blocks
   * save via the validation pass, so display and validation cannot drift.
   */
  get activityTypeMissing() {
    return this.formResponse.merchActivityTypeMissing;
  }

  /**
   * Distinct suppliers (customers) present in the form's product pool, for the
   * per-instance supplier filter. Sorted by name.
   *
   * @returns {{id:number,name:string}[]}
   */
  get supplierOptions() {
    if (!this.form) return [];
    const products = this.form.products || [];
    const byId = new Map();
    products.forEach(p => {
      const id = Number(p && p.supplier_id);
      if (Number.isFinite(id) && id > 0 && !byId.has(id)) {
        byId.set(id, String((p && p.supplier_label) || ''));
      }
    });
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Whether the selected activity type uses the customer/product pickers. An
   * activity with show_product_selection = false (e.g. New Account) hides them.
   * No activity selected, or an unknown one, defaults to allowing them.
   *
   * Delegates to FormResponse (single source of truth shared with validation).
   */
  get selectedActivityAllowsProducts() {
    return this.formResponse.merchSelectedActivityAllowsProducts;
  }

  /**
   * The supplier field is always hidden. The supplier (customers_id) is derived
   * from the selected product, since a product implies its owner and the product
   * picker is searchable by brand. Kept as a getter (returning false) so any
   * remaining template/watcher references resolve to "not shown".
   */
  get showSupplierPicker() {
    return false;
  }

  /**
   * The form's product pool. The product list is NOT filtered by supplier (the
   * supplier follows the product, not the other way around), so all products
   * for the form are always selectable/searchable.
   *
   * @returns {object[]}
   */
  _filteredProducts() {
    return (this.form && this.form.products) || [];
  }

  /**
   * Merchandising-type forms get a per-instance product picker, populated
   * from the response controller's survey-form product overlay
   * (which seeds form.products with the event's program-effective labels).
   *
   * Delegates to FormResponse (single source of truth shared with validation).
   */
  get showProductPicker() {
    return !!(this.form && this.form.isProductScopeForm);
  }

  get productPickerDisabled() {
    if (!this.form || !this.form.isProductScopeForm) {
      return false;
    }
    // Default/seeded (event-assigned) products can't be changed
    if (this.formResponse && !this.formResponse._manuallyAdded) {
      return true;
    }
    return !this.selectedActivityAllowsProducts || this.productOptions.length === 0;
  }

  get showFeedbackField() {
    return !!(this.form && this.form.isSamplingForm && this.form._samplingFeedbackEnabled);
  }

  /**
   * Products available to pick on each instance. Sourced from the form's
   * overlaid products array (which the response controller seeded from the
   * event's merchandising scope). Filtered to brand+name shape and sorted.
   *
   * @returns {{id:number,name:string}[]}
   */
  get productOptions() {
    return this.formResponse.merchProductOptions;
  }

  /**
   * Inline error flag for the product picker. The same condition (on
   * FormResponse) blocks save via the validation pass, so display and
   * validation cannot drift.
   */
  get productMissing() {
    return this.formResponse.merchProductMissing;
  }

  /**
   * Build the VirtualSelect option list for the product picker, grouped into
   * sections by brand. Each section header is the brand name; the option text
   * under it is the product label as stored (product_label_name) with no brand
   * prefix added, so the brand isn't repeated on every line. Products with no
   * brand fall under an "Other" section.
   *
   * Sourced from form.products (seeded by the response controller's survey
   * product overlay), which carries brand_name + product_label_name alongside
   * the id - richer than the flattened productOptions getter, which stays as-is
   * for the visibility/validation checks.
   *
   * @returns {{label:string, options:{label:string,value:string}[]}[]}
   */
  _buildGroupedProductOptions() {
    const products = this._filteredProducts();
    const byBrand = new Map();
    products
      .filter(p => p && Number.isFinite(Number(p.id)) && Number(p.id) > 0)
      .forEach(p => {
        const brand = String(p.brand_name || '').trim() || 'Other';
        if (!byBrand.has(brand)) {
          byBrand.set(brand, []);
        }
        byBrand.get(brand).push({
          // Product label as stored; the brand lives in the group header so we
          // deliberately do not prefix it here.
          label: String(p.product_label_name || p.name || ''),
          value: String(Number(p.id)),
        });
      });
    return [...byBrand.keys()]
      .sort((a, b) => a.localeCompare(b))
      .map(brand => ({
        label: brand,
        options: byBrand.get(brand).sort((a, b) => a.label.localeCompare(b.label)),
      }));
  }

  /**
   * If the form has exactly one linked activity type, set it on the
   * response automatically so the merchandiser doesn't have to. No-op
   * when an id is already present (e.g. server-loaded response) or when
   * there is no single-activity to choose from.
   */
  _applyActivityTypeDefault() {
    if (!this.form || !this.form.isMerchandisingForm) return;
    if (this.formResponse.merchandising_activities_id) return;
    const opts = this.activityTypeOptions;
    if (opts.length === 1) {
      this.formResponse.merchandising_activities_id = opts[0].id;
    }
  }

  /**
   * Derive the supplier (customers_id) from the currently-selected product. The
   * supplier field is hidden and always follows the product (its supplier_id is
   * already on the in-memory product). Used on load (server-loaded responses
   * carry a product but the supplier may not be populated yet) and after the
   * product pool arrives. No-op when the activity type doesn't use products or
   * no product is selected.
   */
  _applySupplierFromProduct() {
    if (!this.form || !this.form.isProductScopeForm) return;
    if (!this.selectedActivityAllowsProducts) return;
    const productId = Number(this.formResponse.product_labels_id) || null;
    if (!productId) return;
    const match = (this.form.products || []).find(p => Number(p && p.id) === productId);
    if (match) {
      this.formResponse.customers_id = Number(match.supplier_id) || null;
    }
  }

  /**
   * Auto-select the sole product option when there's exactly one and none is
   * chosen yet, so the merchandiser doesn't have to. Selecting it also derives
   * the supplier (the supplier field is hidden). No-op once the user has touched
   * the product picker (so an explicit clear sticks) or when the activity type
   * doesn't use products. Mirrors _applyActivityTypeDefault.
   */
  _applySingleOptionDefaults() {
    if (!this.form || !this.form.isProductScopeForm) return;
    if (!this.selectedActivityAllowsProducts) return;

    if (!this._productUserTouched && !this.formResponse.product_labels_id && this.productOptions.length === 1) {
      const only = this.productOptions[0];
      this.formResponse._raw_product_labels_id = only.id;
      const match = (this.form.products || []).find(p => Number(p && p.id) === only.id);
      this.formResponse.product = match || null;
      this.formResponse.customers_id = match ? (Number(match.supplier_id) || null) : null;
    }
  }

  /**
   * Init / refresh the VirtualSelect activity-type picker for this response
   * panel. VirtualSelect mutates the original <select> element; subsequent
   * lookups must go through document.getElementById to land on the post-init
   * widget, not the stale pre-init reference. Mirrors RecapResponseForm's
   * "add additional product" picker plumbing.
   */
  _refreshActivityPicker() {
    if (!this.showActivityTypePicker) {
      console.debug('[merch-picker] activity skip: showActivityTypePicker=false', {
        isMerchandisingForm: this.form && this.form.isMerchandisingForm,
        merchandising_activities: this.form && this.form.merchandising_activities,
        optsLen: this.activityTypeOptions.length,
      });
      return;
    }
    if (typeof VirtualSelect === 'undefined') {
      console.warn('[merch-picker] VirtualSelect global is undefined');
      return;
    }
    const id = `form_${this.form.id}_response_${this.formResponse.id}_activity`;
    let el = document.getElementById(id);
    if (!el) {
      console.debug('[merch-picker] activity el not found id=' + id);
      return;
    }
    const options = this.activityTypeOptions.map(opt => ({
      label: opt.name,
      value: String(opt.id),
    }));
    console.debug('[merch-picker] activity init/refresh', {
      id, optsLen: options.length, inited: this._vsActivityInited,
    });
    if (!this._vsActivityInited) {
      vSelect().init({
        ele: '#' + id,
        options: options,
        search: true,
        placeholder: 'Select activity type...',
        searchPlaceholderText: 'Search activity types...',
        maxWidth: '100%',
      });
      this._vsActivityInited = true;
      el = document.getElementById(id);
      if (!el) return;
    } else if (typeof el.setOptions === 'function') {
      el.setOptions(options);
    }
    // Restore selection in case the picker was re-rendered.
    if (typeof el.setValue === 'function') {
      el.setValue(this.formResponse.merchandising_activities_id
        ? String(this.formResponse.merchandising_activities_id)
        : '');
    }
    if (!this._vsActivityChangeBound) {
      el.addEventListener('change', () => {
        this.formResponse.merchandising_activities_id = el.value
          ? Number(el.value) || null
          : null;
        this.$scope.$applyAsync();
      });
      this._vsActivityChangeBound = true;
    }
  }

  /**
   * Init / refresh the VirtualSelect product picker. Same lifecycle as the
   * activity picker - VirtualSelect plugin manages the DOM, we lookup by id
   * each time. On change we set both the raw id (for serialization) and the
   * resolved product object (so the panel header / existing flows update).
   */
  _refreshProductPicker() {
    if (!this.showProductPicker) {
      console.debug('[merch-picker] product skip: showProductPicker=false', {
        isMerchandisingForm: this.form && this.form.isMerchandisingForm,
        _merchandisingSurvey: this.form && this.form._merchandisingSurvey,
        productsLen: this.form && (this.form.products || []).length,
        optsLen: this.productOptions.length,
      });
      return;
    }
    if (typeof VirtualSelect === 'undefined') return;
    const id = `form_${this.form.id}_response_${this.formResponse.id}_product`;
    let el = document.getElementById(id);
    if (!el) {
      console.debug('[merch-picker] product el not found id=' + id);
      return;
    }
    const options = this._buildGroupedProductOptions();
    const optsLen = options.reduce((sum, g) => sum + (g.options ? g.options.length : 0), 0);
    console.debug('[merch-picker] product init/refresh', {
      id, optsLen, groups: options.length,
    });
    // ng-if destroys + recreates this <select> when the picker hides/shows
    // (e.g. switching to/from a "New Account" activity type). Detect the
    // VirtualSelect state from the live element (setOptions present) rather than
    // a one-time flag, and (re)bind the change handler on each freshly created
    // element - otherwise the re-created plain <select> never gets initialized.
    if (typeof el.setOptions !== 'function') {
      vSelect().init({
        ele: '#' + id,
        options: options,
        search: true,
        placeholder: 'Select product...',
        searchPlaceholderText: 'Search products...',
        maxWidth: '100%',
      });
      el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('change', () => {
        const pid = el.value ? Number(el.value) || null : null;
        // Ignore programmatic setValue echoes (value unchanged) - otherwise the
        // supplier-backfill -> product-refresh -> setValue chain loops forever.
        if (pid === (Number(this.formResponse.product_labels_id) || null)) {
          return;
        }
        this._productUserTouched = true;
        this.formResponse._raw_product_labels_id = pid;
        const match = pid
          ? (this.form.products || []).find(p => Number(p && p.id) === pid)
          : null;
        this.formResponse.product = match || null;
        // The supplier field is hidden; the supplier is always derived from the
        // chosen product (its supplier_id is already on the in-memory product,
        // so no lookup/callback is needed). Clears when the product is cleared.
        this.formResponse.customers_id = match ? (Number(match.supplier_id) || null) : null;
        this.$scope.$applyAsync();
      });
    } else {
      el.setOptions(options);
    }
    if (typeof el.setValue === 'function') {
      const current = this.formResponse.product_labels_id;
      el.setValue(current ? String(current) : '');
    }
    if (typeof el.disable === 'function' && typeof el.enable === 'function') {
      if (this.productPickerDisabled) {
        el.disable();
      } else {
        el.enable();
      }
    }
  }

  get form() {
    return this.formResponseSet.form;
  }

  get formResponse() {
    return this._formResponse;
  }

  set formResponse(formResponse) {
    this._formResponse = formResponse;
  }

  get formResponseSet() {
    return this._formResponseSet;
  }

  set formResponseSet(formResponseSet) {
    this._formResponseSet = formResponseSet;
  }

  get rejectedReason() {
    return this._formResponse._recapResponse.declined_reason;
  }

  get rejectedByName() {
    return this._formResponse._recapResponse.declined_by_name;
  }

  get rejectedAt() {
    let date = this._formResponse._recapResponse.declined_at;
    date = new Date(date);
    date = date.toDateString();
    return date;
  }

  set collapsed(collapsed) {
    this._collapsed = collapsed;
  }

  get collapsed() {
    return this._collapsed;
  }

  get canRemoveResponse() {
    if (!this.form) return false;
    if (this.form.isSurveyForm || this.form.isProductScopeForm) {
      return this.formResponseSet.form_responses.length > 1;
    }
    // Product-form panels only get a Remove button when the recapper added
    // the product themselves via the "Add additional product" picker. Panels
    // for products that came from the event setup have no remove path.
    if (this.form.isProductForm && this.formResponse && this.formResponse._manuallyAdded) {
      return true;
    }
    return false;
  }

  /**
   * Translation keys for the Remove control. Product forms call the entry a
   * "product" so the recapper sees "Remove Product" / "...remove this product?"
   * instead of the survey-oriented "response" wording.
   */
  get removeButtonLabelKey() {
    return (this.form && this.form.isProductForm) ? 'REMOVE_PRODUCT' : 'REMOVE_RESPONSE';
  }

  get removeConfirmKey() {
    return (this.form && this.form.isProductForm) ? 'CONFIRM_REMOVE_PRODUCT' : 'CONFIRM_REMOVE_RESPONSE';
  }

  get visibleResponses() {
    return this._visibleResponses;
  }

  get productName() {
    const product = this.formResponse && this.formResponse.product;
    if (product) {
      return product.name;
    }
    return '';
  }

  isFieldResponseInsideFieldBlock(fieldResponse) {
    return !!(fieldResponse.fieldInstance.events_custom_field_blocks_id);
  }

  toggleCollapsed() {
    this.collapsed = !this.collapsed;
  }

  removeResponse() {
    return this.$translate(this.removeConfirmKey)
      .then(message => this.showConfirmModal(message))
      .then(() => {
        this.formResponseSet.removeResponse(this.formResponse);
      });
  }

  _updateVisibleResponses() {
    // Rendering shares the model's single visibility definition
    // (FormResponse.collectVisibleFieldResponses), which now also applies
    // merch activity-type field visibility. This keeps the rendered fields,
    // validation, and serialization in lockstep so a field hidden by the
    // selected activity type can never block save as a stray required field.
    this._visibleResponses = this.formResponse.collectVisibleFieldResponses();
    if (
      this.form
      && this.form.collapse_product_form
      && this.form.isProductForm
      && this.form.collapse_product_form == 'yes'
    ){
      this.collapsed = true;
    }
  }
}

export default RecapResponseFormResponseController;
